<script lang="ts">
    import Chart from 'chart.js/auto'
    import type { Plugin } from 'chart.js'
    import ChartDataLabels from 'chartjs-plugin-datalabels'
    import dayjs from 'dayjs'
    import { onMount } from 'svelte/internal'
    import { InvoicesRepository } from '../../modules/posApi/invoices/invoices.repository'
    import { InvoicesService } from '../../modules/posApi/invoices/invoices.service'
    import type { Invoice, PaymentMethodsOnInvoices } from '../../modules/posApi/invoices/invoice'
    import type { PaymentMethod } from '../../modules/posApi/payment-methods/payment-methods'
    import DateSelect from '../_common/date-select.svelte'
    import LoadSpinner from '../_common/load-spinner.svelte'
    import Zact from '../_common/z/zact.svelte'
    import Zframe from '../_common/z/zframe.svelte'
    import ZContainer from '../_common/z/z-container.svelte'

    const rInvoices = new InvoicesRepository()
    const sInvoices = new InvoicesService(rInvoices)

    let dates: { from: Date; to: Date } = window.api.utils.dates.currentMonth()

    let containerEvolution: HTMLCanvasElement
    let containerParticipacao: HTMLCanvasElement
    let containerRadar: HTMLCanvasElement
    let containerTarifas: HTMLCanvasElement
    let containerParcelas: HTMLCanvasElement
    let containerRecebido: HTMLCanvasElement
    let show

    let charts: Chart[] = []

    // meios com pouca representatividade são agrupados aqui para manter os gráficos legíveis
    const TOP_METHODS = 6
    const BASE_COLORS = ['#008FFB', '#379E75', '#FEB019', '#775DD0', '#6179F3', '#D0483E']
    const OUTROS_COLOR = '#9AA0A6'
    const MUTED = '#666'
    const GRID = '#555'

    type PaymentWithDate = PaymentMethodsOnInvoices & { paymentMethod?: PaymentMethod; date?: Date }
    type MethodGroup = { label: string; ids: string[] }

    function dateChanged(event) {
        dates = event.detail
        getData()
    }

    async function getData() {
        try {
            show = true
            let invoices: Invoice[] = []

            invoices = await sInvoices.getAll({
                from: dates.from,
                to: dates.to,
                payment: true,
                status: ['closed', 'subsidized', 'partial', 'open', 'credit'],
            })

            const paymentsWithDate = flattenPayments(invoices)
            const methods = removeDuplicates(
                paymentsWithDate.map((p) => p.paymentMethod).filter((m) => !!m),
                'paymentMethodId',
            )
            console.log('data', invoices, methods)

            charts.forEach((c) => c.destroy())
            charts = []

            const groups = buildMethodGroups(methods, paymentsWithDate)

            generateEvolution(paymentsWithDate, groups)
            generateParticipation(paymentsWithDate, groups)
            generateRadar(invoices, groups)
            generateFees(paymentsWithDate, groups)
            generateInstallments(paymentsWithDate)
            generateReceivable(invoices)
        } catch (error) {
            evaluateError(error)
        } finally {
            show = false
        }
    }

    function flattenPayments(invoices: Invoice[]): PaymentWithDate[] {
        let list: PaymentWithDate[] = []
        for (const invoice of invoices) {
            for (const payment of invoice.payments || []) {
                list.push({ ...payment, date: payment.createdAt || invoice.createdAt })
            }
        }
        return list
    }

    function removeDuplicates(myArr, prop) {
        return myArr.filter((obj, pos, arr) => {
            return arr.map((mapObj) => mapObj[prop]).indexOf(obj[prop]) === pos
        })
    }

    function buildMethodGroups(methods: PaymentMethod[], payments: PaymentWithDate[]): MethodGroup[] {
        const withTotal = methods.map((method) => ({
            method,
            value: payments.filter((p) => p.paymentMethodId === method.paymentMethodId).reduce((total, p) => total + p.value, 0),
        }))
        withTotal.sort((a, b) => b.value - a.value)

        const top = withTotal.slice(0, TOP_METHODS)
        const rest = withTotal.slice(TOP_METHODS)

        const groups: MethodGroup[] = top.map((item) => ({ label: item.method.label, ids: [item.method.paymentMethodId] }))
        if (rest.length > 0) {
            groups.push({ label: 'Outros', ids: rest.map((item) => item.method.paymentMethodId) })
        }
        return groups
    }

    function colorsForGroups(groups: MethodGroup[]) {
        let i = 0
        return groups.map((g) => (g.label === 'Outros' ? OUTROS_COLOR : BASE_COLORS[i++ % BASE_COLORS.length]))
    }

    function hexToRgba(hex: string, alpha: number) {
        const n = parseInt(hex.replace('#', ''), 16)
        const r = (n >> 16) & 255
        const g = (n >> 8) & 255
        const b = n & 255
        return `rgba(${r}, ${g}, ${b}, ${alpha})`
    }

    function currency(val: number) {
        return window.configs.currency + Number(val || 0).toFixed(2)
    }

    function sumGroup(payments: PaymentWithDate[], group: MethodGroup, field: 'value' | 'installments' = 'value') {
        return payments.filter((p) => group.ids.includes(p.paymentMethodId)).reduce((total, p) => total + (p[field] || 0), 0)
    }

    // agrupa o período em dias (ou semanas, se o período for longo) para o gráfico de evolução
    function buildBuckets(from: Date, to: Date) {
        const totalDays = Math.ceil((to.getTime() - from.getTime()) / 86400000) + 1
        const stepDays = totalDays > 62 ? 7 : 1
        let buckets: { from: Date; to: Date; label: string }[] = []
        for (let d = new Date(from); d <= to; d.setDate(d.getDate() + stepDays)) {
            const bucketFrom = new Date(d)
            const bucketTo = new Date(d)
            bucketTo.setDate(bucketTo.getDate() + stepDays - 1)
            bucketTo.setHours(23, 59, 59, 999)
            buckets.push({
                from: bucketFrom,
                to: bucketTo,
                label: stepDays === 1 ? dayjs(bucketFrom).format('DD/MM') : 'sem ' + dayjs(bucketFrom).format('DD/MM'),
            })
        }
        return buckets
    }

    async function generateEvolution(payments: PaymentWithDate[], groups: MethodGroup[]) {
        const buckets = buildBuckets(dates.from, dates.to)
        const colors = colorsForGroups(groups)
        const datasets = groups.map((group, i) => ({
            label: group.label,
            data: buckets.map((bucket) =>
                Number(
                    payments
                        .filter((p) => group.ids.includes(p.paymentMethodId) && new Date(p.date) >= bucket.from && new Date(p.date) <= bucket.to)
                        .reduce((total, p) => total + p.value, 0)
                        .toFixed(2),
                ),
            ),
            backgroundColor: colors[i],
            borderRadius: 4,
        }))

        const chart = new Chart(containerEvolution, {
            type: 'bar',
            data: { labels: buckets.map((b) => b.label), datasets },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                interaction: { mode: 'index', intersect: false },
                plugins: {
                    legend: { position: 'top', labels: { color: MUTED } },
                    tooltip: { callbacks: { label: (ctx) => ctx.dataset.label + ': ' + currency(ctx.parsed.y) } },
                },
                scales: {
                    x: { stacked: true, grid: { display: false }, ticks: { color: MUTED } },
                    y: { stacked: true, grid: { display: false }, ticks: { display: false } },
                },
            },
        })
        charts.push(chart)
    }

    async function generateParticipation(payments: PaymentWithDate[], groups: MethodGroup[]) {
        const totals = groups.map((group) => sumGroup(payments, group))
        const totalGeral = totals.reduce((a, b) => a + b, 0)
        const percentages = totals.map((v) => (totalGeral > 0 ? Number(((100 / totalGeral) * v).toFixed(1)) : 0))

        const chart = new Chart(containerParticipacao, {
            type: 'doughnut',
            data: {
                labels: groups.map((g) => g.label),
                datasets: [{ data: percentages, backgroundColor: colorsForGroups(groups), borderColor: 'transparent', borderWidth: 2 }],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: { position: 'top', labels: { color: MUTED } },
                    tooltip: { callbacks: { label: (ctx) => ctx.label + ': ' + ctx.parsed.toFixed(1) + '%' } },
                    datalabels: { color: '#fff', font: { size: 11, weight: 'bold' }, formatter: (val) => (val >= 5 ? val.toFixed(1) + '%' : '') },
                },
            },
            plugins: [ChartDataLabels],
        })
        charts.push(chart)
    }

    async function generateRadar(invoices: Invoice[], groups: MethodGroup[]) {
        // "Outros" mistura meios diferentes, então não faz sentido comparar métricas por ticket/qtde aqui
        const namedGroups = groups.filter((g) => g.label !== 'Outros').slice(0, 5)
        if (namedGroups.length === 0) return

        const rows = namedGroups.map((group) => {
            const fromMethod = invoices.filter((inv) => (inv.payments || []).some((p) => group.ids.includes(p.paymentMethodId)))
            const qtde = fromMethod.length
            const valor = fromMethod.reduce(
                (total, inv) => total + inv.payments.filter((p) => group.ids.includes(p.paymentMethodId)).reduce((t, p) => t + p.value, 0),
                0,
            )
            const ticket = qtde === 0 ? 0 : valor / qtde
            return { label: group.label, ticket, qtde, valor }
        })

        const normalize = (values: number[]) => {
            const max = Math.max(...values, 1)
            return values.map((v) => Number(((v / max) * 100).toFixed(1)))
        }

        const metrics = [
            { name: 'Ticket médio', color: '#008FFB', data: normalize(rows.map((r) => r.ticket)) },
            { name: 'Quantidade', color: '#FEB019', data: normalize(rows.map((r) => r.qtde)) },
            { name: 'Valor total', color: '#379E75', data: normalize(rows.map((r) => r.valor)) },
        ]

        const chart = new Chart(containerRadar, {
            type: 'radar',
            data: {
                labels: rows.map((r) => r.label),
                datasets: metrics.map((m) => ({
                    label: m.name,
                    data: m.data,
                    borderColor: m.color,
                    backgroundColor: hexToRgba(m.color, 0.15),
                    pointBackgroundColor: m.color,
                    pointRadius: 3,
                    borderWidth: 2,
                })),
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: { position: 'top', labels: { color: MUTED } },
                    tooltip: { callbacks: { label: (ctx) => ctx.dataset.label + ': ' + ctx.parsed.r.toFixed(0) + ' / 100' } },
                },
                scales: {
                    r: {
                        min: 0,
                        max: 100,
                        ticks: { display: false, backdropColor: 'transparent' },
                        grid: { color: GRID },
                        angleLines: { color: GRID },
                        pointLabels: { color: MUTED },
                    },
                },
            },
        })
        charts.push(chart)
    }

    async function generateFees(payments: PaymentWithDate[], groups: MethodGroup[]) {
        const rows = groups
            .map((group) => {
                const items = payments.filter((p) => group.ids.includes(p.paymentMethodId))
                const fee = items.reduce((total, p) => total + p.value * ((p.feePercentage || 0) / 100) + (p.feeFixed || 0), 0)
                return { label: group.label, value: Number(fee.toFixed(2)) }
            })
            .sort((a, b) => b.value - a.value)

        const chart = new Chart(containerTarifas, {
            type: 'bar',
            data: { labels: rows.map((r) => r.label), datasets: [{ label: 'Custo em tarifas', data: rows.map((r) => r.value), backgroundColor: '#D0483E', borderRadius: 4 }] },
            options: {
                indexAxis: 'y',
                responsive: true,
                maintainAspectRatio: false,
                // reserva espaço à direita da área de plotagem para o datalabel da barra mais longa não ficar cortado
                layout: { padding: { right: 60 } },
                plugins: {
                    legend: { display: false },
                    tooltip: { callbacks: { label: (ctx) => currency(ctx.parsed.x) } },
                    datalabels: { anchor: 'end', align: 'end', color: MUTED, formatter: (val) => currency(val) },
                },
                scales: {
                    x: { grid: { display: false }, ticks: { color: MUTED } },
                    y: { grid: { display: false }, ticks: { color: MUTED } },
                },
            },
            plugins: [ChartDataLabels],
        })
        charts.push(chart)
    }

    async function generateInstallments(payments: PaymentWithDate[]) {
        let buckets: { label: string; order: number; qtde: number }[] = []
        for (const payment of payments) {
            const n = payment.installments && payment.installments > 0 ? payment.installments : 1
            const order = n >= 10 ? 10 : n
            const label = n >= 10 ? '10x ou mais' : n + 'x'
            let bucket = buckets.find((b) => b.label === label)
            if (!bucket) {
                bucket = { label, order, qtde: 0 }
                buckets.push(bucket)
            }
            bucket.qtde += 1
        }
        buckets.sort((a, b) => a.order - b.order)

        const chart = new Chart(containerParcelas, {
            type: 'bar',
            data: { labels: buckets.map((b) => b.label), datasets: [{ label: 'Operações', data: buckets.map((b) => b.qtde), backgroundColor: '#6179F3', borderRadius: 4 }] },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: { display: false },
                    datalabels: { anchor: 'end', align: 'end', color: MUTED },
                },
                scales: {
                    x: { grid: { display: false }, ticks: { color: MUTED } },
                    y: { grid: { display: false }, ticks: { display: false } },
                },
            },
            plugins: [ChartDataLabels],
        })
        charts.push(chart)
    }

    async function generateReceivable(invoices: Invoice[]) {
        const total = invoices.reduce((t, inv) => t + (inv.total || 0), 0)
        const totalPaid = invoices.reduce((t, inv) => t + (inv.totalPaid || 0), 0)
        const percent = total > 0 ? Math.min(100, (totalPaid / total) * 100) : 0
        const color = percent >= 90 ? '#379E75' : percent >= 70 ? '#FEB019' : '#D0483E'

        const centerText: Plugin<'doughnut'> = {
            id: 'centerText',
            afterDraw(chart) {
                const { ctx, chartArea } = chart
                const x = (chartArea.left + chartArea.right) / 2
                const y = (chartArea.top + chartArea.bottom) / 2
                ctx.save()
                ctx.textAlign = 'center'
                ctx.textBaseline = 'middle'
                ctx.font = 'bold 28px sans-serif'
                ctx.fillStyle = color
                ctx.fillText(percent.toFixed(0) + '%', x, y - 8)
                ctx.font = '13px sans-serif'
                ctx.fillStyle = MUTED
                ctx.fillText('Valor recebido', x, y + 18)
                ctx.restore()
            },
        }

        const chart = new Chart(containerRecebido, {
            type: 'doughnut',
            data: { labels: ['Recebido', 'Restante'], datasets: [{ data: [percent, 100 - percent], backgroundColor: [color, hexToRgba('#999999', 0.2)], borderWidth: 0 }] },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                cutout: '75%',
                plugins: { legend: { display: false }, tooltip: { enabled: false } },
            },
            plugins: [centerText],
        })
        charts.push(chart)
    }

    function evaluateError(error) {
        window.error(error)
    }

    onMount(async () => {
        await getData()
    })
</script>

<div class="base-dash sales-dash">
    <Zact>
        <DateSelect startFrom={dates.from} startTo={dates.to} on:change={dateChanged} />
    </Zact>

    <ZContainer class="h-space">
        <Zframe
            size={12}
            title="Evolução do valor recebido por meio de pagamento"
            headline="Série diária (ou semanal, em períodos mais longos) do valor recebido, separada pelos principais meios de pagamento. Os meios menos representativos são agrupados em 'Outros' para manter o gráfico legível."
        >
            <div class="chart-box">
                <canvas bind:this={containerEvolution}></canvas>
            </div>
        </Zframe>

        <Zframe
            size={6}
            sizeM={12}
            title="Participação por meio de pagamento"
            headline="Percentual do valor total recebido no período que cada meio de pagamento representa."
        >
            <div class="chart-box chart-box_fill">
                <canvas bind:this={containerParticipacao}></canvas>
            </div>
        </Zframe>

        <Zframe
            size={6}
            sizeM={12}
            title="Comparativo entre meios de pagamento"
            headline="Compara ticket médio, quantidade de vendas e valor total dos principais meios de pagamento. Os valores são normalizados (0 a 100) para permitir a comparação entre métricas de escalas diferentes."
        >
            <div class="chart-box chart-box_tall">
                <canvas bind:this={containerRadar}></canvas>
            </div>
        </Zframe>

        <Zframe
            size={6}
            sizeM={12}
            title="Custo em tarifas por meio de pagamento"
            headline="Estimativa do valor perdido em tarifas (percentual + fixa) cobradas em cada meio de pagamento no período."
        >
            <div class="chart-box">
                <canvas bind:this={containerTarifas}></canvas>
            </div>
        </Zframe>

        <Zframe size={6} sizeM={12} title="Distribuição por quantidade de parcelas" headline="Quantidade de operações realizadas para cada número de parcelas.">
            <div class="chart-box">
                <canvas bind:this={containerParcelas}></canvas>
            </div>
        </Zframe>

        <Zframe
            size={6}
            sizeM={12}
            title="Percentual do valor total já recebido"
            headline="Relação entre o valor total faturado no período e o valor efetivamente recebido, considerando vendas parceladas e em aberto."
        >
            <div class="chart-box">
                <canvas bind:this={containerRecebido}></canvas>
            </div>
        </Zframe>
        <div class="z-col"></div>
    </ZContainer>
</div>
<LoadSpinner delay={300} {show} />

<style>
    .chart-box {
        position: relative;
        width: 100%;
        height: 320px;
    }
    .chart-box_tall {
        height: 360px;
    }
    .chart-box_fill {
        height: 100%;
        min-height: 320px;
    }
</style>
